import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/axiosConfig';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Trash2, Upload } from 'lucide-react';

/** Keep in sync with `server/services/shareImagePresetService.js` MAX_IMAGE_BYTES */
const MAX_REFERENCE_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Presets + prompt for AI session share background. Generate calls parent with { presetId?, customPrompt? }.
 */
export default function SessionShareImagePresetModal({
  open,
  onOpenChange,
  onGenerate,
  shareImageWorking,
  actionError,
  t,
}) {
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [promptText, setPromptText] = useState('');
  const [images, setImages] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const runOnlyInputRef = useRef(null);
  /** One-run reference images (not saved); max 5. */
  const [runOnlyItems, setRunOnlyItems] = useState([]);
  const runOnlyKeyRef = useRef(0);

  const revokeRunOnlyUrls = useCallback((items) => {
    (items || []).forEach((x) => {
      if (x?.url) URL.revokeObjectURL(x.url);
    });
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/share-image-presets');
      setPresets(res.data?.presets || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPresetDetail = useCallback(async (presetId) => {
    if (!presetId) {
      setImages([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/share-image-presets/${presetId}`);
      const p = res.data?.preset;
      if (p) {
        setPromptText(p.prompt_text || '');
        setPresetName(p.name || '');
        setImages(Array.isArray(p.images) ? p.images : []);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load preset');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setRunOnlyItems((prev) => {
        revokeRunOnlyUrls(prev);
        return [];
      });
      return;
    }
    loadList();
    setSelectedId('');
    setPromptText('');
    setPresetName('');
    setImages([]);
    setError(null);
    setRunOnlyItems((prev) => {
      revokeRunOnlyUrls(prev);
      return [];
    });
  }, [open, loadList, revokeRunOnlyUrls]);

  useEffect(() => {
    if (!open || selectedId === '') return;
    loadPresetDetail(Number(selectedId));
  }, [open, selectedId, loadPresetDetail]);

  const canGenerate =
    Boolean(selectedId) || promptText.trim().length > 0 || runOnlyItems.length > 0;

  const handleRunOnlyPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const tooBig = picked.find((f) => f.size > MAX_REFERENCE_IMAGE_BYTES);
    if (tooBig) {
      setError(t('activityDetail.sessionShareImagePresetFileTooBig', { maxMb: 5 }));
      return;
    }
    setRunOnlyItems((prev) => {
      const maxAdd = Math.max(0, 5 - prev.length);
      const slice = picked.slice(0, maxAdd);
      const next = [
        ...prev,
        ...slice.map((file) => {
          runOnlyKeyRef.current += 1;
          return { id: runOnlyKeyRef.current, file, url: URL.createObjectURL(file) };
        }),
      ];
      return next;
    });
  };

  const handleRemoveRunOnly = (id) => {
    setRunOnlyItems((prev) => {
      const hit = prev.find((x) => x.id === id);
      if (hit?.url) URL.revokeObjectURL(hit.url);
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleClearRunOnly = () => {
    setRunOnlyItems((prev) => {
      revokeRunOnlyUrls(prev);
      return [];
    });
  };

  const handleCreatePreset = async () => {
    const name = presetName.trim() || t('activityDetail.sessionShareImagePresetUntitled');
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/api/share-image-presets', {
        name,
        prompt_text: promptText.trim(),
      });
      const newId = res.data?.id;
      await loadList();
      if (newId) setSelectedId(String(newId));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePreset = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/share-image-presets/${selectedId}`, {
        name: presetName.trim() || t('activityDetail.sessionShareImagePresetUntitled'),
        prompt_text: promptText.trim(),
      });
      await loadList();
      await loadPresetDetail(Number(selectedId));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedId) return;
    if (!window.confirm(t('activityDetail.sessionShareImagePresetDeleteConfirm'))) return;
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/share-image-presets/${selectedId}`);
      setSelectedId('');
      setPromptText('');
      setPresetName('');
      setImages([]);
      await loadList();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const pid = selectedId ? Number(selectedId) : null;
    if (!pid) {
      setError(t('activityDetail.sessionShareImagePresetSelectFirst'));
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_REFERENCE_IMAGE_BYTES) {
      setError(t('activityDetail.sessionShareImagePresetFileTooBig', { maxMb: 5 }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/share-image-presets/${pid}/images`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadPresetDetail(pid);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      await api.delete(`/api/share-image-presets/${selectedId}/images/${imageId}`);
      await loadPresetDetail(Number(selectedId));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to remove image');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateClick = async () => {
    if (!canGenerate || shareImageWorking) return;
    const payload = {};
    if (selectedId) payload.presetId = Number(selectedId);
    const pt = promptText.trim();
    if (pt.length > 0) payload.customPrompt = pt;
    if (runOnlyItems.length > 0) {
      payload.ephemeralFiles = runOnlyItems.map((x) => x.file);
    }
    await onGenerate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[min(90vw,42rem)] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{t('activityDetail.sessionShareImagePresetModalTitle')}</DialogTitle>
          <DialogDescription>{t('activityDetail.sessionShareImagePresetModalDesc')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {actionError && (
            <p className='text-sm text-destructive' role='alert'>
              {actionError}
            </p>
          )}
          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}

          <div className='space-y-2'>
            <Label>{t('activityDetail.sessionShareImagePresetSelect')}</Label>
            <Select
              value={selectedId || '__none__'}
              onValueChange={(v) => {
                if (v === '__none__') {
                  setSelectedId('');
                  setPromptText('');
                  setPresetName('');
                  setImages([]);
                } else {
                  setSelectedId(v);
                }
              }}
              disabled={loading || saving}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('activityDetail.sessionShareImagePresetPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='__none__'>{t('activityDetail.sessionShareImagePresetCustomOnly')}</SelectItem>
                {presets.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='preset-name'>{t('activityDetail.sessionShareImagePresetName')}</Label>
            <Input
              id='preset-name'
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder={t('activityDetail.sessionShareImagePresetNamePh')}
              disabled={saving}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='preset-prompt'>{t('activityDetail.sessionShareImagePresetPrompt')}</Label>
            <Textarea
              id='preset-prompt'
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              rows={5}
              placeholder={t('activityDetail.sessionShareImagePresetPromptPh')}
              disabled={saving}
              className='resize-y min-h-[100px]'
            />
            <p className='text-xs text-muted-foreground'>{t('activityDetail.sessionShareImagePresetStyleHint')}</p>
            <p className='text-xs text-muted-foreground'>{t('activityDetail.sessionShareImagePresetMaxFileSizeHint', { maxMb: 5 })}</p>
          </div>

          <div className='space-y-2'>
            <Label>{t('activityDetail.sessionShareImagePresetRunOnlyRef')}</Label>
            <p className='text-xs text-muted-foreground'>{t('activityDetail.sessionShareImagePresetRunOnlyRefHint')}</p>
            <div className='flex flex-wrap gap-2'>
              {runOnlyItems.map((item) => (
                <div key={item.id} className='relative w-20 h-20 rounded border border-border overflow-hidden bg-muted'>
                  <img src={item.url} alt='' className='w-full h-full object-cover' />
                  <Button
                    type='button'
                    variant='destructive'
                    size='icon'
                    className='absolute top-0.5 right-0.5 h-6 w-6'
                    onClick={() => handleRemoveRunOnly(item.id)}
                    disabled={saving}
                    aria-label={t('activityDetail.sessionShareImagePresetRemoveImage')}
                  >
                    <Trash2 className='h-3 w-3' />
                  </Button>
                </div>
              ))}
            </div>
            <div className='flex flex-wrap gap-2'>
              <input
                ref={runOnlyInputRef}
                type='file'
                accept='image/png,image/jpeg,image/webp'
                multiple
                className='hidden'
                onChange={handleRunOnlyPick}
                disabled={saving || runOnlyItems.length >= 5}
              />
              <Button
                type='button'
                variant='outline'
                size='sm'
                disabled={saving || runOnlyItems.length >= 5}
                onClick={() => runOnlyInputRef.current?.click()}
                className='inline-flex items-center gap-2'
              >
                <Upload className='h-4 w-4' />
                {t('activityDetail.sessionShareImagePresetRunOnlyUpload')}
              </Button>
              {runOnlyItems.length > 0 ? (
                <Button type='button' variant='ghost' size='sm' onClick={handleClearRunOnly} disabled={saving}>
                  {t('activityDetail.sessionShareImagePresetRunOnlyClear')}
                </Button>
              ) : null}
            </div>
          </div>

          {selectedId && (
            <div className='space-y-2'>
              <Label>{t('activityDetail.sessionShareImagePresetReferenceImages')}</Label>
              <div className='flex flex-wrap gap-2'>
                {images.map((img) => (
                  <div key={img.id} className='relative w-20 h-20 rounded border border-border overflow-hidden bg-muted'>
                    <img
                      src={`/api/share-image-presets/${selectedId}/images/${img.id}/file`}
                      alt=''
                      className='w-full h-full object-cover'
                    />
                    <Button
                      type='button'
                      variant='destructive'
                      size='icon'
                      className='absolute top-0.5 right-0.5 h-6 w-6'
                      onClick={() => handleDeleteImage(img.id)}
                      disabled={saving}
                      aria-label={t('activityDetail.sessionShareImagePresetRemoveImage')}
                    >
                      <Trash2 className='h-3 w-3' />
                    </Button>
                  </div>
                ))}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/png,image/jpeg,image/webp'
                  className='hidden'
                  onChange={handleUpload}
                  disabled={saving}
                />
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={saving}
                  onClick={() => fileInputRef.current?.click()}
                  className='inline-flex items-center gap-2'
                >
                  <Upload className='h-4 w-4' />
                  {t('activityDetail.sessionShareImagePresetUpload')}
                </Button>
              </div>
            </div>
          )}

          <div className='flex flex-wrap gap-2'>
            <Button type='button' variant='secondary' size='sm' onClick={handleCreatePreset} disabled={saving || loading}>
              {t('activityDetail.sessionShareImagePresetNew')}
            </Button>
            {selectedId ? (
              <>
                <Button type='button' variant='outline' size='sm' onClick={handleSavePreset} disabled={saving || loading}>
                  {t('activityDetail.sessionShareImagePresetSave')}
                </Button>
                <Button type='button' variant='outline' size='sm' className='text-destructive' onClick={handleDeletePreset} disabled={saving || loading}>
                  {t('activityDetail.sessionShareImagePresetDelete')}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <DialogFooter className='gap-2 sm:gap-0 flex-col sm:flex-row'>
          <Button type='button' variant='ghost' onClick={() => onOpenChange(false)} disabled={shareImageWorking}>
            {t('activityDetail.sessionShareImagePresetClose')}
          </Button>
          <Button type='button' onClick={handleGenerateClick} disabled={!canGenerate || shareImageWorking || saving}>
            {shareImageWorking ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin mr-2' />
                {t('activityDetail.sessionShareImageGenerating')}
              </>
            ) : (
              t('activityDetail.sessionShareImagePresetGenerate')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
